"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.TRACING_DEFAULTS = void 0;
exports.startIdleSpan = startIdleSpan;

var _currentScopes = require("../currentScopes.js");

var _debugBuild = require("../debug-build.js");

var _semanticAttributes = require("../semanticAttributes.js");

var _logger = require("../utils-hoist/logger.js");

var _time = require("../utils-hoist/time.js");

var _hasTracingEnabled = require("../utils/hasTracingEnabled.js");

var _spanOnScope = require("../utils/spanOnScope.js");

var _spanUtils = require("../utils/spanUtils.js");

var _sentryNonRecordingSpan = require("./sentryNonRecordingSpan.js");

var _spanstatus = require("./spanstatus.js");

var _trace = require("./trace.js");

function _toArray(arr) { return _arrayWithHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

const TRACING_DEFAULTS = exports.TRACING_DEFAULTS = {
  idleTimeout: 1000,
  finalTimeout: 30000,
  childSpanTimeout: 15000
};
const FINISH_REASON_HEARTBEAT_FAILED = 'heartbeatFailed';
const FINISH_REASON_IDLE_TIMEOUT = 'idleTimeout';
const FINISH_REASON_FINAL_TIMEOUT = 'finalTimeout';
const FINISH_REASON_EXTERNAL_FINISH = 'externalFinish';
/**
 * An idle span is a span that automatically finishes. It does this by tracking child spans as activities.
 * An idle span is always the active span.
 */

function startIdleSpan(startSpanOptions, options = {}) {
  // Activities store a list of active spans
  const activities = new Map(); // We should not use heartbeat if we finished a span

  let _finished = false; // Timer that tracks idleTimeout

  let _idleTimeoutID; // The reason why the span was finished


  let _finishReason = FINISH_REASON_EXTERNAL_FINISH;

  let _autoFinishAllowed = !options.disableAutoFinish;

  const _cleanupHooks = [];
  const _options$idleTimeout = options.idleTimeout,
        idleTimeout = _options$idleTimeout === void 0 ? TRACING_DEFAULTS.idleTimeout : _options$idleTimeout,
        _options$finalTimeout = options.finalTimeout,
        finalTimeout = _options$finalTimeout === void 0 ? TRACING_DEFAULTS.finalTimeout : _options$finalTimeout,
        _options$childSpanTim = options.childSpanTimeout,
        childSpanTimeout = _options$childSpanTim === void 0 ? TRACING_DEFAULTS.childSpanTimeout : _options$childSpanTim,
        beforeSpanEnd = options.beforeSpanEnd;
  const client = (0, _currentScopes.getClient)();

  if (!client || !(0, _hasTracingEnabled.hasTracingEnabled)()) {
    return new _sentryNonRecordingSpan.SentryNonRecordingSpan();
  }

  const scope = (0, _currentScopes.getCurrentScope)();
  const previousActiveSpan = (0, _spanUtils.getActiveSpan)();

  const span = _startIdleSpan(startSpanOptions); // We patch span.end to ensure we can run some things before the span is ended
  // eslint-disable-next-line @typescript-eslint/unbound-method


  span.end = new Proxy(span.end, {
    apply(target, thisArg, args) {
      if (beforeSpanEnd) {
        beforeSpanEnd(span);
      } // Just ensuring that this keeps working, even if we ever have more arguments here


      const _args = _toArray(args),
            definedEndTimestamp = _args[0],
            rest = _args.slice(1);

      const timestamp = definedEndTimestamp || (0, _time.timestampInSeconds)();
      const spanEndTimestamp = (0, _spanUtils.spanTimeInputToSeconds)(timestamp); // Ensure we end with the last span timestamp, if possible

      const spans = (0, _spanUtils.getSpanDescendants)(span).filter(child => child !== span); // If we have no spans, we just end, nothing else to do here

      if (!spans.length) {
        onIdleSpanEnded(spanEndTimestamp);
        return Reflect.apply(target, thisArg, [spanEndTimestamp, ...rest]);
      }

      const childEndTimestamps = spans.map(span => (0, _spanUtils.spanToJSON)(span).timestamp).filter(timestamp => !!timestamp);
      const latestSpanEndTimestamp = childEndTimestamps.length ? Math.max(...childEndTimestamps) : undefined; // In reality this should always exist here, but type-wise it may be undefined...

      const spanStartTimestamp = (0, _spanUtils.spanToJSON)(span).start_timestamp; // The final endTimestamp should:
      // * Never be before the span start timestamp
      // * Be the latestSpanEndTimestamp, if there is one, and it is smaller than the passed span end timestamp
      // * Otherwise be the passed end timestamp
      // Final timestamp can never be after finalTimeout

      const endTimestamp = Math.min(spanStartTimestamp ? spanStartTimestamp + finalTimeout / 1000 : Infinity, Math.max(spanStartTimestamp || -Infinity, Math.min(spanEndTimestamp, latestSpanEndTimestamp || Infinity)));
      onIdleSpanEnded(endTimestamp);
      return Reflect.apply(target, thisArg, [endTimestamp, ...rest]);
    }

  });
  /**
   * Cancels the existing idle timeout, if there is one.
   */

  function _cancelIdleTimeout() {
    if (_idleTimeoutID) {
      clearTimeout(_idleTimeoutID);
      _idleTimeoutID = undefined;
    }
  }
  /**
   * Restarts idle timeout, if there is no running idle timeout it will start one.
   */


  function _restartIdleTimeout(endTimestamp) {
    _cancelIdleTimeout();

    _idleTimeoutID = setTimeout(() => {
      if (!_finished && activities.size === 0 && _autoFinishAllowed) {
        _finishReason = FINISH_REASON_IDLE_TIMEOUT;
        span.end(endTimestamp);
      }
    }, idleTimeout);
  }
  /**
   * Restarts child span timeout, if there is none running it will start one.
   */


  function _restartChildSpanTimeout(endTimestamp) {
    _idleTimeoutID = setTimeout(() => {
      if (!_finished && _autoFinishAllowed) {
        _finishReason = FINISH_REASON_HEARTBEAT_FAILED;
        span.end(endTimestamp);
      }
    }, childSpanTimeout);
  }
  /**
   * Start tracking a specific activity.
   * @param spanId The span id that represents the activity
   */


  function _pushActivity(spanId) {
    _cancelIdleTimeout();

    activities.set(spanId, true);
    const endTimestamp = (0, _time.timestampInSeconds)(); // We need to add the timeout here to have the real endtimestamp of the idle span
    // Remember timestampInSeconds is in seconds, timeout is in ms

    _restartChildSpanTimeout(endTimestamp + childSpanTimeout / 1000);
  }
  /**
   * Remove an activity from usage
   * @param spanId The span id that represents the activity
   */


  function _popActivity(spanId) {
    if (activities.has(spanId)) {
      activities.delete(spanId);
    }

    if (activities.size === 0) {
      const endTimestamp = (0, _time.timestampInSeconds)(); // We need to add the timeout here to have the real endtimestamp of the idle span
      // Remember timestampInSeconds is in seconds, timeout is in ms

      _restartIdleTimeout(endTimestamp + idleTimeout / 1000);
    }
  }

  function onIdleSpanEnded(endTimestamp) {
    _finished = true;
    activities.clear();

    _cleanupHooks.forEach(cleanup => cleanup());

    (0, _spanOnScope._setSpanForScope)(scope, previousActiveSpan);
    const spanJSON = (0, _spanUtils.spanToJSON)(span);
    const startTimestamp = spanJSON.start_timestamp; // This should never happen, but to make TS happy...

    if (!startTimestamp) {
      return;
    }

    const attributes = spanJSON.data || {};

    if (!attributes[_semanticAttributes.SEMANTIC_ATTRIBUTE_SENTRY_IDLE_SPAN_FINISH_REASON]) {
      span.setAttribute(_semanticAttributes.SEMANTIC_ATTRIBUTE_SENTRY_IDLE_SPAN_FINISH_REASON, _finishReason);
    }

    _logger.logger.log(`[Tracing] Idle span "${spanJSON.op}" finished`);

    const childSpans = (0, _spanUtils.getSpanDescendants)(span).filter(child => child !== span);
    let discardedSpans = 0;
    childSpans.forEach(childSpan => {
      // We cancel all pending spans with status "cancelled" to indicate the idle span was finished early
      if (childSpan.isRecording()) {
        childSpan.setStatus({
          code: _spanstatus.SPAN_STATUS_ERROR,
          message: 'cancelled'
        });
        childSpan.end(endTimestamp);
        _debugBuild.DEBUG_BUILD && _logger.logger.log('[Tracing] Cancelling span since span ended early', JSON.stringify(childSpan, undefined, 2));
      }

      const childSpanJSON = (0, _spanUtils.spanToJSON)(childSpan);
      const _childSpanJSON$timest = childSpanJSON.timestamp,
            childEndTimestamp = _childSpanJSON$timest === void 0 ? 0 : _childSpanJSON$timest,
            _childSpanJSON$start_ = childSpanJSON.start_timestamp,
            childStartTimestamp = _childSpanJSON$start_ === void 0 ? 0 : _childSpanJSON$start_;
      const spanStartedBeforeIdleSpanEnd = childStartTimestamp <= endTimestamp; // Add a delta with idle timeout so that we prevent false positives

      const timeoutWithMarginOfError = (finalTimeout + idleTimeout) / 1000;
      const spanEndedBeforeFinalTimeout = childEndTimestamp - childStartTimestamp <= timeoutWithMarginOfError;

      if (_debugBuild.DEBUG_BUILD) {
        const stringifiedSpan = JSON.stringify(childSpan, undefined, 2);

        if (!spanStartedBeforeIdleSpanEnd) {
          _logger.logger.log('[Tracing] Discarding span since it happened after idle span was finished', stringifiedSpan);
        } else if (!spanEndedBeforeFinalTimeout) {
          _logger.logger.log('[Tracing] Discarding span since it finished after idle span final timeout', stringifiedSpan);
        }
      }

      if (!spanEndedBeforeFinalTimeout || !spanStartedBeforeIdleSpanEnd) {
        (0, _spanUtils.removeChildSpanFromSpan)(span, childSpan);
        discardedSpans++;
      }
    });

    if (discardedSpans > 0) {
      span.setAttribute('sentry.idle_span_discarded_spans', discardedSpans);
    }
  }

  _cleanupHooks.push(client.on('spanStart', startedSpan => {
    // If we already finished the idle span,
    // or if this is the idle span itself being started,
    // or if the started span has already been closed,
    // we don't care about it for activity
    if (_finished || startedSpan === span || !!(0, _spanUtils.spanToJSON)(startedSpan).timestamp) {
      return;
    }

    const allSpans = (0, _spanUtils.getSpanDescendants)(span); // If the span that was just started is a child of the idle span, we should track it

    if (allSpans.includes(startedSpan)) {
      _pushActivity(startedSpan.spanContext().spanId);
    }
  }));

  _cleanupHooks.push(client.on('spanEnd', endedSpan => {
    if (_finished) {
      return;
    }

    _popActivity(endedSpan.spanContext().spanId);
  }));

  _cleanupHooks.push(client.on('idleSpanEnableAutoFinish', spanToAllowAutoFinish => {
    if (spanToAllowAutoFinish === span) {
      _autoFinishAllowed = true;

      _restartIdleTimeout();

      if (activities.size) {
        _restartChildSpanTimeout();
      }
    }
  })); // We only start the initial idle timeout if we are not delaying the auto finish


  if (!options.disableAutoFinish) {
    _restartIdleTimeout();
  }

  setTimeout(() => {
    if (!_finished) {
      span.setStatus({
        code: _spanstatus.SPAN_STATUS_ERROR,
        message: 'deadline_exceeded'
      });
      _finishReason = FINISH_REASON_FINAL_TIMEOUT;
      span.end();
    }
  }, finalTimeout);
  return span;
}

function _startIdleSpan(options) {
  const span = (0, _trace.startInactiveSpan)(options);
  (0, _spanOnScope._setSpanForScope)((0, _currentScopes.getCurrentScope)(), span);
  _debugBuild.DEBUG_BUILD && _logger.logger.log('[Tracing] Started span is an idle span');
  return span;
}